import { Logger, processUnionEntry, type NString, type Nullable } from '@mobalytics/shared';
import { BattlePassRewardsAccess, type TreasuryPaymentsPrice } from '../../../__generated/treasury/types';
import { treasuryApolloClient } from '../../../treasury-apollo-client';
import { processAllowedActions } from '../../payment/utils/process-allowed-actions.utils';
import { usePaymentV2PageQuery } from '../_gql/__generated/payment-v2-page.query.gql.generated';
import { BillingInfoT, CustomerT, type ProductI } from '../payment.types';
import { processBattlePassProduct } from '../utils/process-product-battle-pass';
import { processProductXPBooster } from '../utils/process-product-xp-booster';

interface ParamsT {
  productId: string;
  priceId: string | undefined;
  productAbbreviation: NString;
}

type ReturnT =
  | {
      status: 'loading';
    }
  | {
      status: 'error';
      errorMessage: string;
    }
  | {
      status: 'success';
      product: ProductI;
      paypal: {
        email: string;
      } | null;
    };

export const usePaymentV2PageData = (params: ParamsT): ReturnT => {
  const { productId, priceId, productAbbreviation } = params;
  const { data, error, loading } = usePaymentV2PageQuery({
    client: treasuryApolloClient,
    errorPolicy: 'all',
    variables: {
      productFilter: { id: productId },
      battlePassInput: { id: productId },
      allowedActionsInput: {
        productAbbreviation: productAbbreviation!,
      },
    },
    skip: !productId || !productAbbreviation,
  });

  if (loading) {
    return {
      status: 'loading',
    };
  }

  if (error) {
    Logger.error('[usePaymentV2PageData]: Error fetching payment page data', error);
    return {
      status: 'error',
      errorMessage: 'Internal server error',
    };
  }
  if (data?.treasury?.payments?.product?.error) {
    Logger.error('[usePaymentV2PageData]: Product error', data?.treasury?.payments?.product?.error);
    return {
      status: 'error',
      errorMessage: 'Internal error',
    };
  }

  // process products
  const rawProduct = data?.treasury?.payments?.product?.data;

  if (!rawProduct) {
    Logger.error('[usePaymentV2PageData]: Product not found', data);
    return {
      status: 'error',
      errorMessage: 'Internal error',
    };
  }

  const customer = processUnionEntry<CustomerT>(data?.treasury?.payments?.customer, 'TreasuryPaymentsCustomer');
  const billingInfo = processUnionEntry<BillingInfoT>(customer?.billingInfo, 'TreasuryPaymentsBillingInfo');
  const paypal = billingInfo?.paypal?.email ? { email: billingInfo?.paypal?.email } : null;

  if (rawProduct.__typename === 'TreasuryProductBattlePass') {
    const price = processPrice(rawProduct.prices, priceId);
    if (!price) {
      Logger.error('[usePaymentV2PageData]: price not found');
      return {
        status: 'error',
        errorMessage: 'Internal error',
      };
    }
    const isCreateSubscriptionAllowed = !!processAllowedActions(customer?.allowedActions).create;

    return {
      status: 'success',
      product: processBattlePassProduct(
        rawProduct,
        data.battlePass.battlePass.data?.enrollment?.rewardsAccess ?? BattlePassRewardsAccess.REGULAR,
        isCreateSubscriptionAllowed,
        price
      ),
      paypal,
    };
  }

  if (rawProduct.__typename === 'TreasuryProductXPBooster') {
    const price = processPrice(rawProduct.prices, priceId);
    if (!price) {
      Logger.error('[usePaymentV2PageData]: price not found');
      return {
        status: 'error',
        errorMessage: 'Internal error',
      };
    }
    const battlePassId = rawProduct.battlePass?.id;
    if (!battlePassId) {
      Logger.error('[usePaymentV2PageData]: battle pass id for xp booster not found');
      return {
        status: 'error',
        errorMessage: 'Internal error',
      };
    }
    const product = processProductXPBooster(rawProduct, price, battlePassId);
    return {
      status: 'success',
      product,
      paypal,
    };
  }

  return {
    status: 'error',
    errorMessage: 'Unsupported product type',
  };
};

const processPrice = <T extends Pick<TreasuryPaymentsPrice, 'id' | 'basePrice'>>(
  prices: Nullable<T[]>,
  priceId: NString
): T | null => {
  if (!prices) {
    return null;
  }
  return prices?.find((it) => it.id === priceId) ?? prices[0] ?? null;
};
